import React from 'react';
import type { Guest } from '@/types/guest.types';


interface GuestListProps {
  guests: Guest[];
  onInviteCheckboxChange: (guestId: number, checked: boolean) => void;
  selectedInviteIds: number[];
  onSendInvitation?: (guest: Guest) => void;
  hideInviteActions?: boolean;
  showSelection?: boolean;
  onGuestClick?: (guest: Guest) => void;
  onEditGuest?: (guest: Guest) => void;
  onDeleteGuest?: (guest: Guest) => void;
  /** Map of guestId → seat label (e.g. "T3 · S2") */
  seatAssignments?: Map<number, string>;
}

const GuestItem: React.FC<{
  guest: Guest;
  isSelected: boolean;
  onToggle: (guestId: number, checked: boolean) => void;
  onSendInvitation?: (guest: Guest) => void;
  hideInviteActions?: boolean;
  showSelection?: boolean;
  onGuestClick?: (guest: Guest) => void;
  onEditGuest?: (guest: Guest) => void;
  onDeleteGuest?: (guest: Guest) => void;
  seatLabel?: string;
}> = ({ guest, isSelected, onToggle, onSendInvitation, hideInviteActions, showSelection = true, onGuestClick, onEditGuest, onDeleteGuest, seatLabel }) => (
  <div className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md">
    <div className="flex items-center gap-2">
      {showSelection && (
        <input
          type="checkbox"
          title={`Select ${guest.name}`}
          checked={isSelected}
          onChange={(e) => onToggle(guest.id, e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          disabled={false}
        />
      )}
      <div>
        <button
          type="button"
          className="font-medium text-gray-900 hover:underline focus:outline-none"
          onClick={() => onGuestClick && onGuestClick(guest)}
        >
          {guest.name}
        </button>
        <div className="text-xs text-gray-500">
          Status: <span className="capitalize">{guest.rsvp_status}</span>
        </div>
        {seatLabel ? (
          <div className="text-xs text-blue-600 font-medium mt-0.5">
            🪺 {seatLabel}
          </div>
        ) : (
          <div className="text-xs text-gray-400 mt-0.5">— no seat</div>
        )}
      </div>
    </div>
    <div className="flex gap-2">
      {!hideInviteActions && onSendInvitation && (
        <button
          className="px-2 py-1 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition"
          onClick={() => onSendInvitation(guest)}
        >
          {guest.rsvp_status === 'invited' ? 'Resend' : 'Send Invite'}
        </button>
      )}
      {onEditGuest && (
        <button
          className="px-2 py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition"
          onClick={() => onEditGuest(guest)}
        >
          Edit
        </button>
      )}
      {onDeleteGuest && (
        <button
          className="px-2 py-1 rounded bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition"
          onClick={() => onDeleteGuest(guest)}
        >
          Delete
        </button>
      )}
    </div>
  </div>
);

export const GuestList = React.memo<GuestListProps>(({ guests, onInviteCheckboxChange, selectedInviteIds, onSendInvitation, hideInviteActions, showSelection = true, onGuestClick, onEditGuest, onDeleteGuest, seatAssignments }) => (
  <div className="space-y-2">
    {guests.map((guest) => (
      <GuestItem
        key={guest.id}
        guest={guest}
        isSelected={selectedInviteIds.includes(guest.id)}
        onToggle={onInviteCheckboxChange}
        onSendInvitation={onSendInvitation}
        hideInviteActions={hideInviteActions}
        showSelection={showSelection}
        onGuestClick={onGuestClick}
        onEditGuest={onEditGuest}
        onDeleteGuest={onDeleteGuest}
        seatLabel={seatAssignments?.get(guest.id)}
      />
    ))}
  </div>
));